'use client';

import React, {useState} from 'react';
import {Link} from '@/i18n/navigation';
import {Typography} from '@mui/material';
import {trackCtaClick} from '@/lib/analytics';
import {StyledHeroCtaCardIcon, StyledHeroCtaCardRoot} from '@/components/sections/hero-media-section/HeroCtaCard.styles';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {ModalSectionItem} from '@/lib/ensure-link-button';
import PopupSectionModal from '@/components/ui/modal/PopupSectionModal';

type HeroCtaCardProps = {
  heading: string;
  iconUrl: string;
  href: string;
  isExternal?: boolean;
  modalSections?: ReadonlyArray<ModalSectionItem | null> | null;
  focalX?: number | null;
  focalY?: number | null;
  contentId: string;
};

const HeroCtaCard = React.memo(function HeroCtaCard({
  heading,
  iconUrl,
  href,
  isExternal,
  modalSections,
  focalX,
  focalY,
  contentId,
}: HeroCtaCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const hasModalSections = Boolean(modalSections && modalSections.some(Boolean));

  const inner = (
    <StyledHeroCtaCardRoot>
      <StyledHeroCtaCardIcon className='hero-cta-card__icon'>
        <ContentfulImage
          src={iconUrl}
          alt={heading}
          fill
          sizes='96px'
          style={{objectFit: 'cover'}}
          focalX={focalX}
          focalY={focalY}
        />
      </StyledHeroCtaCardIcon>
      <Typography className='label' variant='h4' component='p'>
        {heading}
      </Typography>
    </StyledHeroCtaCardRoot>
  );

  // Modal sections take precedence over any link configuration on the card.
  if (hasModalSections) {
    const handleModalClick = () => {
      trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'modal', contentId});
      setModalOpen(true);
    };

    return (
      <>
        <button
          type='button'
          onClick={handleModalClick}
          aria-label={heading}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 'none',
            background: 'none',
            textAlign: 'inherit',
            cursor: 'pointer',
          }}
        >
          {inner}
        </button>
        <PopupSectionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sections={modalSections!}
          ariaLabel={heading}
        />
      </>
    );
  }

  const handleClick = () => {
    trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'cta_card', contentId});
  };

  if (isExternal) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={{textDecoration: 'none'}}
        onClick={handleClick}
        aria-label={heading}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link href={href} style={{textDecoration: 'none'}} onClick={handleClick} aria-label={heading}>
      {inner}
    </Link>
  );
});

export default HeroCtaCard;
